const SAMPLE_KIT_HOVER_PREVIEW_CONFIG = Object.freeze({
  arrowInset: 22,
  gap: 14,
  viewportPadding: 16,
});

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max);

class AostaSampleKitPicker extends HTMLElement {
  connectedCallback() {
    this.field = this.querySelector('[data-sample-kit-field]');
    this.countField = this.querySelector('[data-sample-kit-count-field]');
    this.validationControl = this.querySelector('[data-sample-kit-validation-control]');
    this.status = this.querySelector('[data-sample-kit-status]');
    this.error = this.querySelector('[data-sample-kit-error]');
    this.fieldset = this.querySelector('.aosta-sample-kit__fieldset');
    this.slots = Array.from(this.querySelectorAll('[data-sample-kit-slot]'));
    this.slotViewport = this.querySelector('[data-sample-kit-slot-viewport]');
    this.groupSelects = Array.from(this.querySelectorAll('[data-sample-kit-group-select]'));
    this.sampleInputs = Array.from(this.querySelectorAll('[data-sample-kit-sample-input]'));
    this.slotNavButtons = Array.from(this.querySelectorAll('[data-sample-kit-slot-nav]'));
    this.clearButtons = Array.from(this.querySelectorAll('[data-sample-kit-clear]'));
    this.min = Number(this.dataset.min) || 1;
    this.max = Number(this.dataset.max) || this.min;
    this.currentSlotIndex = 0;
    this.form = null;
    this.productForm = null;
    this.hoverPreview = null;
    this.hoverPreviewImage = null;
    this.activePreviewTrigger = null;

    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleInvalid = this.handleInvalid.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerOut = this.handlePointerOut.bind(this);
    this.handlePointerOver = this.handlePointerOver.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleSlotNav = this.handleSlotNav.bind(this);
    this.handleSlotScroll = this.handleSlotScroll.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.groupSelects.forEach((select) => select.addEventListener('change', this.handleChange));
    this.sampleInputs.forEach((input) => input.addEventListener('change', this.handleChange));
    this.slotViewport?.addEventListener('scroll', this.handleSlotScroll, { passive: true });
    this.slotNavButtons.forEach((button) => button.addEventListener('click', this.handleSlotNav));
    this.clearButtons.forEach((button) => button.addEventListener('click', this.handleClear));
    this.validationControl?.addEventListener('invalid', this.handleInvalid);

    if (this.canShowHoverPreview()) {
      this.addEventListener('pointerover', this.handlePointerOver);
      this.addEventListener('pointermove', this.handlePointerMove);
      this.addEventListener('pointerout', this.handlePointerOut);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.ensureHoverPreview();
    }

    this.bindFormListeners();
    requestAnimationFrame(() => this.bindFormListeners());
    this.updateState();
  }

  disconnectedCallback() {
    this.groupSelects?.forEach((select) => select.removeEventListener('change', this.handleChange));
    this.sampleInputs?.forEach((input) => input.removeEventListener('change', this.handleChange));
    this.slotViewport?.removeEventListener('scroll', this.handleSlotScroll);
    this.slotNavButtons?.forEach((button) => button.removeEventListener('click', this.handleSlotNav));
    this.clearButtons?.forEach((button) => button.removeEventListener('click', this.handleClear));
    this.validationControl?.removeEventListener('invalid', this.handleInvalid);
    this.removeEventListener('pointerover', this.handlePointerOver);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerout', this.handlePointerOut);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.form?.removeEventListener('submit', this.handleSubmit, { capture: true });
    this.productForm?.removeEventListener('submit', this.handleSubmit, { capture: true });
    this.hideHoverPreview();
    this.hoverPreview?.remove();
    this.hoverPreview = null;
    this.hoverPreviewImage = null;
  }

  bindFormListeners() {
    const nextForm = document.getElementById(this.dataset.formId || '');
    if (nextForm === this.form) return;

    this.form?.removeEventListener('submit', this.handleSubmit, { capture: true });
    this.productForm?.removeEventListener('submit', this.handleSubmit, { capture: true });

    this.form = nextForm;
    this.productForm = this.form?.closest('product-form-component') || null;
    this.form?.addEventListener('submit', this.handleSubmit, { capture: true });
    this.productForm?.addEventListener('submit', this.handleSubmit, { capture: true });
  }

  handleChange(event) {
    const target = event.target;
    const slot = target instanceof HTMLElement ? target.closest('[data-sample-kit-slot]') : null;

    if (target?.matches?.('[data-sample-kit-group-select]') && slot) {
      this.clearSlotSample(slot);
    }

    this.hideHoverPreview();
    this.updateState();
  }

  handleClear(event) {
    const target = event.currentTarget;
    const slot = target instanceof HTMLElement ? target.closest('[data-sample-kit-slot]') : null;
    if (!slot) return;

    this.clearSlotSample(slot);
    this.updateState();
    this.focusFirstAvailableSample(slot);
  }

  handleSlotNav(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const direction = button.dataset.sampleKitSlotNav === 'previous' ? -1 : 1;
    const nextIndex = this.currentSlotIndex + direction;
    const nextSlot = this.slots[nextIndex];

    if (!nextSlot || nextSlot.hidden) return;

    this.hideHoverPreview();
    this.goToSlot(nextIndex);
  }

  handleSlotScroll() {
    this.syncCurrentSlotFromScroll();

    window.requestAnimationFrame(() => this.updateSlotNavigation());
  }

  handleInvalid(event) {
    event.preventDefault();
    this.showError();
    this.focusFirstIncompleteSlot();
    this.scrollIntoViewIfNeeded();
  }

  handleSubmit(event) {
    if (this.isValid()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.showError();
    this.focusFirstIncompleteSlot();
    this.scrollIntoViewIfNeeded();
  }

  canShowHoverPreview() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover)').matches &&
      Boolean(this.querySelector('[data-sample-kit-sample-option][data-preview-image]'))
    );
  }

  ensureHoverPreview() {
    if (this.hoverPreview) return;

    const preview = document.createElement('div');
    preview.className = 'variant-picker-hover-preview';
    preview.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.alt = '';
    image.loading = 'lazy';

    preview.append(image);
    document.body.append(preview);

    this.hoverPreview = preview;
    this.hoverPreviewImage = image;
  }

  getPreviewTrigger(target) {
    if (!target) return null;

    const trigger = target.closest('[data-sample-kit-sample-option][data-preview-image]');
    return trigger instanceof HTMLElement && this.contains(trigger) ? trigger : null;
  }

  getPreviewImageSource(trigger) {
    return trigger.dataset.previewImage || null;
  }

  showHoverPreview(trigger) {
    const previewImage = this.getPreviewImageSource(trigger);
    if (!previewImage) {
      this.hideHoverPreview();
      return;
    }

    this.ensureHoverPreview();
    if (!this.hoverPreview || !this.hoverPreviewImage) return;

    this.activePreviewTrigger = trigger;
    this.hoverPreviewImage.alt = trigger.dataset.previewAlt || trigger.dataset.sampleLabel || '';
    this.hoverPreview.classList.remove('is-visible');
    this.hoverPreviewImage.removeAttribute('src');
    this.positionHoverPreview(trigger);

    const showLoadedPreview = () => {
      if (this.activePreviewTrigger !== trigger) return;

      this.hoverPreview?.classList.add('is-visible');
      this.positionHoverPreview(trigger);
    };

    const hideFailedPreview = () => {
      if (this.activePreviewTrigger === trigger) {
        this.hideHoverPreview();
      }
    };

    this.hoverPreviewImage.addEventListener('load', showLoadedPreview, { once: true });
    this.hoverPreviewImage.addEventListener('error', hideFailedPreview, { once: true });
    this.hoverPreviewImage.src = previewImage;

    if (this.hoverPreviewImage.complete && this.hoverPreviewImage.naturalWidth > 0) {
      showLoadedPreview();
    }
  }

  hideHoverPreview() {
    this.activePreviewTrigger = null;
    this.hoverPreview?.classList.remove('is-visible');
  }

  positionHoverPreview(trigger) {
    if (!this.hoverPreview) return;

    const rect = trigger.getBoundingClientRect();
    const previewRect = this.hoverPreview.getBoundingClientRect();
    const { arrowInset, gap, viewportPadding } = SAMPLE_KIT_HOVER_PREVIEW_CONFIG;
    const triggerCenter = rect.left + rect.width / 2;
    const spaceAbove = rect.top - viewportPadding;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const fitsAbove = spaceAbove >= previewRect.height + gap;
    const fitsBelow = spaceBelow >= previewRect.height + gap;

    let left = rect.left + rect.width / 2 - previewRect.width / 2;
    const maxLeft = Math.max(viewportPadding, window.innerWidth - previewRect.width - viewportPadding);
    let top;
    let previewPosition;

    left = clampValue(left, viewportPadding, maxLeft);

    if (fitsAbove || (!fitsBelow && spaceAbove >= spaceBelow)) {
      top = Math.max(viewportPadding, rect.top - previewRect.height - gap);
      previewPosition = 'above';
    } else {
      top = Math.min(rect.bottom + gap, window.innerHeight - previewRect.height - viewportPadding);
      previewPosition = 'below';
    }

    const arrowOffset = clampValue(triggerCenter - left, arrowInset, previewRect.width - arrowInset);

    this.hoverPreview.style.left = `${left}px`;
    this.hoverPreview.style.top = `${top}px`;
    this.hoverPreview.style.setProperty('--preview-arrow-offset', `${arrowOffset}px`);
    this.hoverPreview.dataset.previewPosition = previewPosition;
  }

  handlePointerOver(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    this.showHoverPreview(trigger);
  }

  handlePointerMove() {
    if (!this.activePreviewTrigger) return;
    this.positionHoverPreview(this.activePreviewTrigger);
  }

  handlePointerOut(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.hideHoverPreview();
  }

  handleFocusIn(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    this.showHoverPreview(trigger);
  }

  handleFocusOut(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = this.getPreviewTrigger(target);
    if (!trigger) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && trigger.contains(relatedTarget)) return;

    this.hideHoverPreview();
  }

  updateState() {
    this.updateSlotVisibility();
    this.updateSampleOptions();
    this.updateDuplicateOptions();
    this.updatePreviews();
    requestAnimationFrame(() => this.updateSlotNavigation());

    const selected = this.getSelectedEntries();
    const selectedCount = selected.length;

    if (this.field) {
      this.field.value = selected.map((entry) => entry.summary).join(' | ');
    }

    if (this.countField) {
      this.countField.value = selectedCount ? selectedCount.toString() : '';
    }

    if (this.status) {
      this.status.textContent = `${selectedCount} de ${this.max} selecionadas`;
    }

    this.updateNativeValidity(selectedCount);

    if (this.isValid(selectedCount)) {
      this.hideError();
    }
  }

  updateSlotVisibility() {
    this.slots.forEach((slot, index) => {
      const previousSlot = this.slots[index - 1];
      const showSlot = index === 0 || this.isSlotComplete(previousSlot) || this.isSlotComplete(slot);
      const groupSelect = slot.querySelector('[data-sample-kit-group-select]');

      slot.hidden = !showSlot;

      if (groupSelect instanceof HTMLSelectElement) {
        groupSelect.disabled = !showSlot;
      }
    });

    if (this.slots[this.currentSlotIndex]?.hidden) {
      this.goToSlot(this.getLastVisibleSlotIndex(), false);
    }
  }

  updateSampleOptions() {
    this.slots.forEach((slot) => {
      const groupSelect = slot.querySelector('[data-sample-kit-group-select]');
      const sublevel = slot.querySelector('[data-sample-kit-sublevel]');
      const selectedGroup = groupSelect instanceof HTMLSelectElement ? groupSelect.value : '';
      const isComplete = this.isSlotComplete(slot);

      if (sublevel instanceof HTMLElement) {
        sublevel.hidden = !selectedGroup || isComplete || slot.hidden;
      }

      this.getSlotSampleOptions(slot).forEach((option) => {
        const input = option.querySelector('[data-sample-kit-sample-input]');
        const matchesGroup = Boolean(selectedGroup) && option.dataset.groupKey === selectedGroup;

        option.hidden = !matchesGroup;
        option.setAttribute('aria-disabled', matchesGroup ? 'false' : 'true');

        if (input instanceof HTMLInputElement) {
          input.disabled = !matchesGroup || slot.hidden;
        }
      });
    });
  }

  updateDuplicateOptions() {
    const selectedValues = this.getSelectedEntries().map((entry) => entry.value);

    this.slots.forEach((slot) => {
      const currentEntry = this.getSlotEntry(slot);

      this.getSlotSampleOptions(slot).forEach((option) => {
        const input = option.querySelector('[data-sample-kit-sample-input]');
        if (!(input instanceof HTMLInputElement) || option.hidden) return;

        const selectedElsewhere = selectedValues.includes(input.value) && input.value !== currentEntry?.value;

        input.disabled = selectedElsewhere;
        option.setAttribute('aria-disabled', selectedElsewhere ? 'true' : 'false');
      });
    });
  }

  updateSlotNavigation() {
    this.slotNavButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const direction = button.dataset.sampleKitSlotNav;
      const isPrevious = direction === 'previous';
      const targetIndex = this.currentSlotIndex + (isPrevious ? -1 : 1);
      const currentSlot = this.slots[this.currentSlotIndex];
      const targetSlot = this.slots[targetIndex];
      const currentSlotComplete = this.isSlotComplete(currentSlot);
      const targetSlotComplete = this.isSlotComplete(targetSlot);

      button.disabled = isPrevious
        ? targetIndex < 0 || !targetSlot || targetSlot.hidden
        : !currentSlot || !targetSlot || targetSlot.hidden || (!currentSlotComplete && !targetSlotComplete);

      if (!isPrevious) {
        button.textContent = targetIndex >= this.max ? 'Máximo de amostras selecionado' : 'Ir para a próxima amostra';
      }
    });
  }

  syncCurrentSlotFromScroll() {
    if (!(this.slotViewport instanceof HTMLElement)) return;

    let closestIndex = this.currentSlotIndex;
    let closestDistance = Infinity;

    this.slots.forEach((slot, index) => {
      if (slot.hidden) return;

      const distance = Math.abs(slot.offsetLeft - this.slotViewport.scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    this.currentSlotIndex = closestIndex;
  }

  updatePreviews() {
    this.slots.forEach((slot) => {
      const entry = this.getSlotEntry(slot);
      const preview = slot.querySelector('[data-sample-kit-preview]');
      const previewImage = slot.querySelector('[data-sample-kit-preview-image]');
      const previewInitial = slot.querySelector('[data-sample-kit-preview-initial]');
      const previewGroup = slot.querySelector('[data-sample-kit-preview-group]');
      const previewName = slot.querySelector('[data-sample-kit-preview-name]');

      if (!preview) return;

      if (!entry) {
        preview.hidden = true;
        return;
      }

      preview.hidden = false;

      if (previewName) previewName.textContent = entry.sampleLabel;
      if (previewGroup) previewGroup.textContent = entry.groupLabel;
      if (previewInitial) previewInitial.textContent = entry.sampleLabel.slice(0, 1).toUpperCase();

      if (previewImage instanceof HTMLImageElement) {
        if (entry.imageSource) {
          previewImage.src = entry.imageSource;
          previewImage.alt = entry.sampleLabel;
          previewImage.hidden = false;
          if (previewInitial) previewInitial.hidden = true;
        } else {
          previewImage.removeAttribute('src');
          previewImage.alt = '';
          previewImage.hidden = true;
          if (previewInitial) previewInitial.hidden = false;
        }
      }
    });
  }

  clearSlot(slot) {
    const groupSelect = slot?.querySelector('[data-sample-kit-group-select]');

    if (groupSelect instanceof HTMLSelectElement) {
      groupSelect.value = '';
    }

    this.clearSlotSample(slot);
  }

  clearSlotSample(slot) {
    this.getSlotSampleInputs(slot).forEach((input) => {
      input.checked = false;
    });
  }

  goToSlot(index, smooth = true) {
    const slot = this.slots[index];
    if (!(this.slotViewport instanceof HTMLElement) || !slot || slot.hidden) return;

    this.currentSlotIndex = index;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.slotViewport.scrollTo({
      left: slot.offsetLeft,
      behavior: smooth && !prefersReducedMotion ? 'smooth' : 'auto',
    });

    this.updateSlotNavigation();
  }

  getLastVisibleSlotIndex() {
    for (let index = this.slots.length - 1; index >= 0; index -= 1) {
      if (!this.slots[index].hidden) return index;
    }

    return 0;
  }

  getSlotSampleOptions(slot) {
    if (!slot) return [];
    return Array.from(slot.querySelectorAll('[data-sample-kit-sample-option]'));
  }

  getSlotSampleInputs(slot) {
    if (!slot) return [];
    return Array.from(slot.querySelectorAll('[data-sample-kit-sample-input]')).filter(
      (input) => input instanceof HTMLInputElement
    );
  }

  getSlotEntry(slot) {
    if (!slot) return null;

    const groupSelect = slot.querySelector('[data-sample-kit-group-select]');
    const selectedInput = this.getSlotSampleInputs(slot).find((input) => input.checked);
    const selectedOption = selectedInput?.closest('[data-sample-kit-sample-option]');

    if (!(groupSelect instanceof HTMLSelectElement) || !groupSelect.value || !selectedInput || !selectedOption) {
      return null;
    }

    const selectedGroupOption = groupSelect.options[groupSelect.selectedIndex];
    const groupLabel = selectedGroupOption?.textContent?.trim() || selectedOption.dataset.groupLabel || 'Tecido';
    const sampleLabel = selectedOption.dataset.sampleLabel || selectedInput.value;
    const imageSource = selectedOption.dataset.thumbImage || selectedOption.dataset.previewImage || '';
    const slotIndex = Number(slot.dataset.slotIndex || '0') + 1;

    return {
      groupLabel,
      imageSource,
      sampleLabel,
      summary: `Amostra ${slotIndex}: ${groupLabel} / ${sampleLabel}`,
      value: selectedInput.value,
    };
  }

  getSelectedEntries() {
    return this.slots.map((slot) => this.getSlotEntry(slot)).filter(Boolean);
  }

  isSlotComplete(slot) {
    return Boolean(this.getSlotEntry(slot));
  }

  getFirstSequentialGapSlot() {
    let firstIncompleteSlot = null;

    for (const slot of this.slots) {
      if (this.isSlotComplete(slot)) {
        if (firstIncompleteSlot) return firstIncompleteSlot;
      } else if (!firstIncompleteSlot) {
        firstIncompleteSlot = slot;
      }
    }

    return null;
  }

  getValidationState(selectedCount = this.getSelectedEntries().length) {
    const firstSequentialGapSlot = this.getFirstSequentialGapSlot();

    if (firstSequentialGapSlot) {
      return {
        isValid: false,
        message: this.getSequenceErrorMessage(firstSequentialGapSlot),
      };
    }

    if (selectedCount < this.min) {
      return {
        isValid: false,
        message: this.getMinErrorMessage(),
      };
    }

    return {
      isValid: true,
      message: '',
    };
  }

  isValid(selectedCount) {
    return this.getValidationState(selectedCount).isValid;
  }

  updateNativeValidity(selectedCount) {
    if (!this.validationControl) return;

    const validationState = this.getValidationState(selectedCount);
    this.validationControl.value = validationState.isValid ? 'valid' : '';
    this.validationControl.setCustomValidity(validationState.message);
  }

  showError() {
    if (!this.error) return;

    this.error.hidden = false;
    this.error.textContent = this.getValidationState().message;
    this.fieldset?.setAttribute('aria-invalid', 'true');
  }

  hideError() {
    if (!this.error) return;

    this.error.hidden = true;
    this.fieldset?.removeAttribute('aria-invalid');
  }

  focusFirstIncompleteSlot() {
    const firstIncomplete =
      this.getFirstSequentialGapSlot() || this.slots.find((slot) => !slot.hidden && !this.isSlotComplete(slot));
    if (!firstIncomplete) return;

    this.goToSlot(this.slots.indexOf(firstIncomplete));

    const groupSelect = firstIncomplete.querySelector('[data-sample-kit-group-select]');
    if (groupSelect instanceof HTMLSelectElement && !groupSelect.value) {
      groupSelect.focus();
      return;
    }

    this.focusFirstAvailableSample(firstIncomplete);
  }

  focusFirstAvailableSample(slot) {
    const firstSample = this.getSlotSampleInputs(slot).find((input) => !input.disabled);
    firstSample?.focus();
  }

  scrollIntoViewIfNeeded() {
    const rect = this.getBoundingClientRect();
    const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (isVisible) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }

  getMinErrorMessage() {
    return this.dataset.minError || `Selecione pelo menos ${this.min} amostra para adicionar o kit ao carrinho.`;
  }

  getSequenceErrorMessage(slot) {
    const sampleNumber = Number(slot?.dataset.slotIndex || '0') + 1;
    return (
      this.dataset.sequenceError?.replace('{{ sample }}', sampleNumber.toString()) ||
      `Amostra ${sampleNumber} está incompleta. Complete essa amostra antes de adicionar o kit ao carrinho.`
    );
  }
}

if (!customElements.get('aosta-sample-kit-picker')) {
  customElements.define('aosta-sample-kit-picker', AostaSampleKitPicker);
}
